import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Raven from 'raven-js';
// @ts-ignore not yet migrated
import { fetchContentSchema } from 'page-editor-ui/api/requestClients';
// @ts-ignore not yet migrated
import { fetchContentSchemaSucceeded } from 'ContentEditorUI/redux/actions/appActions';
// @ts-ignore not yet migrated
import { getEditingInfo } from 'ContentEditorUI/redux/selectors/editingInfoSelectors';
const AI_EDITS_PARAM = 'ai-edits';
const POLLING_INTERVAL_MS = 5000;
const MAX_POLLING_DURATION_MS = 900000; // 15 minutes

const getIsAiEditsQueryParamEnabled = () => new URLSearchParams(window.location.search).get(AI_EDITS_PARAM) === 'true';
export const useAiEditsPolling = () => {
  const dispatch = useDispatch();
  const editingInfo = useSelector(getEditingInfo);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const isActive = getIsAiEditsQueryParamEnabled();
  const showConfirmModal = isActive && !isConfirmed && !isDismissed;

  // Last value we've accepted as ground truth (from Redux state or after dispatch)
  const confirmedTicksRef = useRef((editingInfo === null || editingInfo === void 0 ? void 0 : editingInfo.last_edit_ticks) != null ? String(editingInfo.last_edit_ticks) : null);
  // Candidate value seen once; must repeat before we dispatch
  const pendingTicksRef = useRef(null);

  // Keep confirmedTicksRef in sync with Redux state. When an auto-save completes
  // (CONTENT_SAVE_REQUEST_SUCCEEDED) or a schema refresh updates editingInfo
  // (FETCH_CONTENT_SCHEMA_SUCCEEDED), the server now has this timestamp — so
  // clearing pending prevents a race where the poll sees the new timestamp before
  // the save response arrives and incorrectly treats it as another external edit.
  useEffect(() => {
    if ((editingInfo === null || editingInfo === void 0 ? void 0 : editingInfo.last_edit_ticks) != null) {
      confirmedTicksRef.current = String(editingInfo.last_edit_ticks);
      pendingTicksRef.current = null;
    }
  }, [editingInfo === null || editingInfo === void 0 ? void 0 : editingInfo.last_edit_ticks]);
  useEffect(() => {
    if (!getIsAiEditsQueryParamEnabled() || !isConfirmed) {
      return;
    }
    const intervalId = setInterval(async () => {
      try {
        var _result$context;
        const result = await fetchContentSchema();
        const serverTicks = result === null || result === void 0 || (_result$context = result.context) === null || _result$context === void 0 || (_result$context = _result$context.editing_info) === null || _result$context === void 0 ? void 0 : _result$context.last_edit_ticks;
        if (!serverTicks) return;
        const serverTicksStr = String(serverTicks);
        if (serverTicksStr === confirmedTicksRef.current) {
          // Server matches our confirmed state — no new edit
          pendingTicksRef.current = null;
          return;
        }
        if (confirmedTicksRef.current === null) {
          // Bootstrap: no confirmed state yet — seed without dispatching
          confirmedTicksRef.current = serverTicksStr;
          return;
        }
        if (pendingTicksRef.current !== serverTicksStr) {
          // First observation of a new value — wait for confirmation next poll
          pendingTicksRef.current = serverTicksStr;
          return;
        }

        // Same new value in two consecutive polls — confirmed external edit
        confirmedTicksRef.current = serverTicksStr;
        pendingTicksRef.current = null;
        dispatch(fetchContentSchemaSucceeded(result));
      } catch (error) {
        Raven.captureMessage('[AI Edits] Error polling for content updates', {
          extra: {
            error
          }
        });
      }
    }, POLLING_INTERVAL_MS);
    const timeoutId = setTimeout(() => clearInterval(intervalId), MAX_POLLING_DURATION_MS);
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [dispatch, isConfirmed]);
  const onConfirm = useCallback(() => setIsConfirmed(true), []);
  const onDismiss = useCallback(() => setIsDismissed(true), []);
  return {
    showConfirmModal,
    onConfirm,
    onDismiss
  };
};